<script lang="ts">
  import WorkoutPlayerIcon from './WorkoutPlayerIcon.svelte';
  import { formatDistance, formatDuration } from '../../lib/format';
  import { type WorkoutPlayer, type WorkoutPlayerType } from '@/Util/api';
  import { formatXSSDetails, handleImageError, type XSSDetails } from '@/Util/utils';
  import * as Popover from '$lib/components/ui/popover';
  import { Button } from '@/lib/components/ui/button';
  import { workoutPlayerTypeToName } from '@/Common/constants';
  import HelpTooltip from '@/Common/HelpTooltip.svelte';
  import { format } from 'date-fns';
  import { Activity } from '@/Common/ActivityFunctions';
  import { connectedWorkoutPlayers } from '@/Singletons/WorkoutPlayersWritable';

  export let workoutPlayer: WorkoutPlayer | null;
  export let onSendNew: (workoutPlayer: WorkoutPlayerType) => void;

  let disabled = false;
  $: disabled = workoutPlayer?.status === 'sending' || workoutPlayer?.status === 'connecting';

  $: otherDevices = workoutPlayer
    ? $connectedWorkoutPlayers.players
        .map((wp) => wp.type)
        .filter((type) => type !== workoutPlayer?.type)
    : [];

  let xss: XSSDetails<string> = formatXSSDetails({
    xss: 0,
    xlss: 0,
    xhss: 0,
    xpss: 0,
  });

  $: xss = formatXSSDetails({
    xss: workoutPlayer?.workout?.xss ?? 0,
    xlss: workoutPlayer?.workout?.xlss ?? 0,
    xhss: workoutPlayer?.workout?.xhss ?? 0,
    xpss: workoutPlayer?.workout?.xpss ?? 0,
  });

  let copyOpen = false;

  function handleResend() {
    if (workoutPlayer?.workout) {
      // Resend a mixed mode workout the same way it was sent.
      connectedWorkoutPlayers.sendWorkoutToWorkoutPlayer(workoutPlayer.type, workoutPlayer.workout, workoutPlayer.workout.slope_intervals ?? undefined);
    }
  }

  const slopeIntervalsLabel = {
    free_ride: { text: 'Slopes as free ride · ERG on', help: 'This mixed mode workout was sent with its slope intervals as free ride intervals, which have no power target. Ride the rest of the workout in ERG mode.' },
    power: { text: 'Slopes as power targets · ERG off', help: 'This mixed mode workout was sent with power targets for its slope intervals. Ride the whole workout with ERG mode off and shift gears to hold each target.' },
  };

  function handleCopyTo(target: WorkoutPlayerType) {
    if (workoutPlayer?.workout) {
      connectedWorkoutPlayers.sendWorkoutToWorkoutPlayer(target, workoutPlayer.workout);
    }
  }

  const statusToName: { [key in 'sending' | 'reseting' | 'sent' | 'received'  | 'failed' | 'connecting']: string } = {
    sending: 'Sending…',
    reseting: 'Reseting…',
    sent: 'Sent',
    received: 'Received',
    failed: 'Failed',
    connecting: 'Connecting...'
  };

  function deviceToHelpMessage(workoutPlayer: WorkoutPlayerType): string {
    if (workoutPlayer === 'garmin_connect') return "Don’t see the workout in Garmin Connect? Check under <em>Training & Planning > Workouts</em>. If it’s missing, resend it and refresh the app.";
    if (workoutPlayer === 'xert_ebc') return "Not seeing the workout? Refresh the app, resend it from here, or restart Xert EBC.";
    return `Workout missing in ${workoutPlayerTypeToName[workoutPlayer]}? Resend it and relaunch the app.`;
  };
</script>

<!-- TODO (hyde): improve button design and interaction on these workout player cards -->
{#if workoutPlayer}
  <div class="rounded-lg border border-gray-200 bg-white p-4 shadow-sm hover:shadow max-w-[400px]">
    <div class="mb-3 flex items-center justify-between">
      <div class="flex items-center gap-3">
        <WorkoutPlayerIcon type={workoutPlayer.type} />
        <div>
          <div class="font-medium">{workoutPlayerTypeToName[workoutPlayer.type]}</div>
          {#if workoutPlayer.status !== 'sent' || workoutPlayer.workout}
            <div class="text-xs text-gray-500">
              <span>{statusToName[workoutPlayer.status]}</span>
              {workoutPlayer.workout?.sent_at ? ` • ${format(new Date(workoutPlayer.workout?.sent_at), 'MMM d, h:mm a')}` : ''}
            </div>
          {/if}
        </div>
        <HelpTooltip message={deviceToHelpMessage(workoutPlayer.type)} />
      </div>

      <div class="relative">
        {#if workoutPlayer.workout}
          <Button variant='outline' {disabled} on:click={handleResend}>Resend</Button>
        {/if}
      </div>
    </div>

    {#if workoutPlayer.status !== 'connecting'}
      {#if workoutPlayer.workout}
        <div class="grid grid-cols-1 gap-2">
          <div>
            {#if workoutPlayer.workout.training_source}
              <span class="rounded bg-blue-100 px-2 py-0.5 font-medium text-blue-800">{workoutPlayer.workout.training_source}</span>
            {/if}
            {#if workoutPlayer.workout.slope_intervals && slopeIntervalsLabel[workoutPlayer.workout.slope_intervals]}
              <div class="flex items-center text-xs text-gray-600">
                <span class="rounded bg-amber-100 px-2 py-0.5 font-medium text-amber-800">{slopeIntervalsLabel[workoutPlayer.workout.slope_intervals].text}</span>
                <HelpTooltip message={slopeIntervalsLabel[workoutPlayer.workout.slope_intervals].help} />
              </div>
            {/if}
            <div class="text-sm font-medium">{workoutPlayer.workout.name}</div>
            <div class="text-xs text-gray-600">{workoutPlayer.workout.type ? workoutPlayer.workout.type + ' • ' : ''}{formatDuration(workoutPlayer.workout.duration ?? 0)} • {formatDistance(workoutPlayer.workout.distance ?? 0)}</div>
          </div>
          <img class='rounded-md border border-border w-full h-[150px]' src={Activity.getOneImage(workoutPlayer.workout)?.url ?? Activity.getBackupImageUrl()} alt="Chart" draggable="false" on:error={handleImageError} />
          <div class="grid grid-cols-4 gap-2 text-center text-sm">
            <div class="rounded bg-gray-50 p-2">
              <div class="font-semibold">{xss.xss}</div>
              <div class="text-gray-500 text-xs">Total</div>
            </div>
            <div class="rounded bg-gray-50 p-2">
              <div class="font-semibold text-[var(--color-xlss)]">{xss.xlss}</div>
              <div class="text-gray-500 text-xs">Low</div>
            </div>
            <div class="rounded bg-gray-50 p-2">
              <div class="font-semibold text-[var(--color-xhss)]">{xss.xhss}</div>
              <div class="text-gray-500 text-xs">High</div>
            </div>
            <div class="rounded bg-gray-50 p-2">
              <div class="font-semibold text-[var(--color-xpss)]">{xss.xpss}</div>
              <div class="text-gray-500 text-xs">Peak</div>
            </div>
          </div>
        </div>
      {:else}
        <div class="text-sm text-gray-500">No workout loaded</div>
      {/if}

      <div class="mt-3 flex flex-wrap gap-2">
        <Button variant='outline' {disabled} on:click={() => onSendNew(workoutPlayer.type)}>Choose Workout</Button>

        {#if workoutPlayer.workout}
          <Popover.Root portal="#shadcn-ui-dialog-target" bind:open={copyOpen}>
            <Popover.Trigger asChild let:builder>
              <Button variant='outline' {disabled} builders={[builder]}>Copy</Button>
            </Popover.Trigger>
            <Popover.Content class="flex flex-col gap-1 p-0 w-auto min-w-[150px]">
              {#if otherDevices.length === 0}
                <div class="p-2 text-sm text-gray-500">No other connected devices</div>
              {:else}
                {#each otherDevices as d}
                  <Button
                    variant="ghost"
                    class="flex w-full"
                    on:click={() => {
                      copyOpen = false;
                      handleCopyTo(d);
                    }}
                  >
                    <span>{workoutPlayerTypeToName[d]}</span>
                  </Button>
                {/each}
              {/if}
            </Popover.Content>
          </Popover.Root>
        {/if}

        <Button variant='outline' {disabled}
          on:click={() => connectedWorkoutPlayers.sendWorkoutToWorkoutPlayer(workoutPlayer.type, null)}
        >Reset</Button>
      </div>
    {/if}
  </div>
{/if}
